import { Component } from '@theme/component';

/**
 * Expands and collapses long product descriptions with an animated height change.
 */
class ProductDescriptionToggle extends Component {
  requiredRefs = ['content', 'button'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {Animation | undefined} */
  #animation;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.enabled !== 'true') return;

    requestAnimationFrame(() => this.#measure());
    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this.refs.content);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#animation?.cancel();
  }

  updatedCallback() {
    super.updatedCallback();
    requestAnimationFrame(() => this.#measure());
  }

  #getCollapsedHeight() {
    const { content } = this.refs;
    const textElement = content.querySelector('.text-block') ?? content;
    const styles = getComputedStyle(textElement);
    const fontSize = Number.parseFloat(styles.fontSize);
    const computedLineHeight = Number.parseFloat(styles.lineHeight);
    const lineHeight = Number.isNaN(computedLineHeight) ? fontSize * 1.5 : computedLineHeight;
    const visibleLines = Number.parseInt(this.dataset.visibleLines ?? '5', 10);

    return lineHeight * visibleLines;
  }

  #measure() {
    if (this.dataset.enabled !== 'true' || this.dataset.expanded === 'true' || this.#animation) return;

    const { content, button } = this.refs;
    this.dataset.ready = 'true';

    const collapsedHeight = this.#getCollapsedHeight();
    const hasOverflow = content.scrollHeight > collapsedHeight + 1;

    button.hidden = !hasOverflow;
    if (!hasOverflow) this.removeAttribute('data-ready');
  }

  /** @param {Event} event */
  toggle = (event) => {
    event.preventDefault();

    const { content, button } = this.refs;
    const expanding = this.dataset.expanded !== 'true';
    const startHeight = content.getBoundingClientRect().height;
    const endHeight = expanding ? content.scrollHeight : this.#getCollapsedHeight();

    this.#animation?.cancel();
    content.style.maxHeight = 'none';
    content.style.height = `${startHeight}px`;
    content.style.overflow = 'hidden';
    this.dataset.expanded = expanding ? 'true' : 'false';
    button.setAttribute('aria-expanded', String(expanding));

    this.#animation = content.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      {
        duration: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 320,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }
    );

    this.#animation.onfinish = () => {
      content.style.removeProperty('height');
      content.style.removeProperty('max-height');
      content.style.removeProperty('overflow');
      this.#animation = undefined;
    };
  };
}

if (!customElements.get('product-description-toggle')) {
  customElements.define('product-description-toggle', ProductDescriptionToggle);
}
